import {MAIN_ROOM_ID} from '../constants';
import {fetchWithInfoLog} from '../fetcher';
import {assertRoomID} from '../typeGuards';
import type {
    APIType,
    ClientProps,
    GenerateEndpoint,
    GetEndpointParams,
} from '../types';

/**
 * Creates breakout-room action handlers.
 *
 * These actions map to the Pexip REST API breakout room functions
 * (`/api/client/v2/conferences/<alias>/breakouts/...`).
 * All actions are host-only unless otherwise noted.
 *
 * @see {@link https://docs.pexip.com/api_client/api_rest.htm | Pexip client REST API}
 */
export const createBreakoutsActions = ({
    clientProps,
    API,
    generateEndpoint,
}: {
    clientProps: ClientProps;
    API: APIType;
    generateEndpoint: GenerateEndpoint;
}) => {
    type Defaults = Pick<
        ClientProps,
        'conferenceAlias' | 'host' | 'breakoutUuid'
    >;
    const withDefaults = <T>(p: T): Defaults & T =>
        ({
            conferenceAlias: clientProps.conferenceAlias,
            host: clientProps.host,
            breakoutUuid: clientProps.breakoutUuid,
            ...p,
        }) as Defaults & T;

    /**
     * Creates a new breakout room.
     *
     * @returns The UUID of the newly created room.
     *
     * REST: `POST /breakouts`
     */
    const breakout = generateEndpoint({
        func: async ([params]: GetEndpointParams<'breakout'>, fetcher) => {
            const res = await API.breakouts({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                },
                body: params,
                host: clientProps.host,
            });
            const {result} = res.data;
            assertRoomID(result.breakout_uuid);
            return {breakout_uuid: result.breakout_uuid};
        },
        funcName: 'breakout',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Signals that a participant needs help in a breakout room.
     *
     * If called by a host within a breakout room (`breakoutUuid` set and
     * not the main room), uses the host-level endpoint. Otherwise falls
     * back to the guest endpoint.
     *
     * REST: `POST /breakouts/<uuid>/breakoutbuzz` or
     *       `POST /breakoutbuzz`
     */
    const breakoutAskForHelp = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'breakoutAskForHelp'>,
            fetcher,
        ) => {
            const {conferenceAlias, breakoutUuid, host} = withDefaults(params);

            if (breakoutUuid && breakoutUuid !== MAIN_ROOM_ID) {
                return API.hostBreakoutBuzz({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.guestBreakoutBuzz({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'breakoutAskForHelp',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Moves participants between breakout rooms or from the main room.
     *
     * When `fromBreakoutUuid` is provided, participants are moved from that
     * breakout room. Otherwise they are moved from the main room.
     *
     * REST: `POST /breakouts/<uuid>/participants/breakout` or
     *       `POST /breakout_move_participants`
     */
    const breakoutMoveParticipants = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'breakoutMoveParticipants'>,
            fetcher,
        ) => {
            const {fromBreakoutUuid, toRoomUuid, participants} = params;

            return fromBreakoutUuid
                ? await API.breakoutMoveParticipants({
                      fetcher,
                      params: {
                          conferenceAlias: clientProps.conferenceAlias,
                          breakoutUuid: fromBreakoutUuid,
                      },
                      body: {
                          breakout_uuid: toRoomUuid,
                          participants: participants,
                      },
                      host: clientProps.host,
                  })
                : await API.breakoutMoveParticipantsFromMain({
                      fetcher,
                      params: {
                          conferenceAlias: clientProps.conferenceAlias,
                      },
                      body: {
                          breakout_uuid: toRoomUuid,
                          participants: participants,
                      },
                      host: clientProps.host,
                  });
        },
        funcName: 'breakoutMoveParticipants',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Clears the "ask for help" signal for a breakout room.
     *
     * Routing mirrors {@link breakoutAskForHelp}: host-level endpoint
     * when inside a breakout room, guest endpoint otherwise.
     *
     * REST: `POST /breakouts/<uuid>/clearbreakoutbuzz` or
     *       `POST /clearbreakoutbuzz`
     */
    const breakoutRemoveAskForHelp = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'breakoutRemoveAskForHelp'>,
            fetcher,
        ) => {
            const {conferenceAlias, breakoutUuid, host} = withDefaults(params);

            if (breakoutUuid && breakoutUuid !== MAIN_ROOM_ID) {
                return API.hostClearBreakoutBuzz({
                    fetcher,
                    params: {
                        conferenceAlias,
                        breakoutUuid,
                    },
                    host,
                });
            }

            return API.guestClearBreakoutBuzz({
                fetcher,
                params: {
                    conferenceAlias,
                },
                host,
            });
        },
        funcName: 'breakoutRemoveAskForHelp',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Requests a guest token for a breakout room.
     *
     * This is a retriable action — if the token request fails transiently,
     * it will be queued for retry.
     *
     * REST: `POST /breakouts/<uuid>/request_token`
     */
    const breakoutRequestGuestToken = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'breakoutRequestGuestToken'>,
            fetcher,
        ) => {
            const {conferenceAlias, host, breakoutUuid} = withDefaults(params);

            if (!breakoutUuid) {
                throw new Error('Missing breakoutUuid');
            }

            return API.breakoutRequestGuestToken({
                fetcher,
                params: {
                    conferenceAlias,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'breakoutRequestGuestToken',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    /**
     * Disconnects a single breakout room. Respects the room's `end_action`
     * setting to determine where participants are moved.
     *
     * REST: `POST /breakouts/<uuid>/disconnect`
     */
    const closeBreakoutRoom = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'closeBreakoutRoom'>,
            fetcher,
        ) => {
            return API.disconnect({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                    breakoutUuid: params.breakoutUuid,
                },
                host: clientProps.host,
            });
        },
        funcName: 'closeBreakoutRoom',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Disconnects all breakout rooms and returns participants to the main room.
     *
     * REST: `POST /breakouts/disconnect`
     */
    const closeBreakouts = generateEndpoint({
        func: async (_params: GetEndpointParams<'closeBreakouts'>, fetcher) => {
            return API.breakoutsDisconnect({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                },
                host: clientProps.host,
            });
        },
        funcName: 'closeBreakouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Moves all participants from every breakout room back to the main room,
     * leaving the empty rooms open.
     *
     * REST: `POST /breakouts/empty`
     */
    const emptyBreakouts = generateEndpoint({
        func: async (_params: GetEndpointParams<'emptyBreakouts'>, fetcher) => {
            return API.breakoutsEmpty({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                },
                host: clientProps.host,
            });
        },
        funcName: 'emptyBreakouts',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /**
     * Allows a guest participant to leave the breakout room and return
     * to the main conference.
     *
     * This is the only breakout action available to guests (when the room
     * permits it via `guests_allowed_to_leave`).
     *
     * REST: `POST /leavebreakout`
     */
    const guestLeaveBreakoutRoom = generateEndpoint({
        func: async (
            _params: GetEndpointParams<'guestLeaveBreakoutRoom'>,
            fetcher,
        ) => {
            return API.guestLeaveBreakout({
                fetcher,
                params: {
                    conferenceAlias: clientProps.conferenceAlias,
                },
                host: clientProps.host,
            });
        },
        funcName: 'guestLeaveBreakoutRoom',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    return {
        breakout,
        breakoutAskForHelp,
        breakoutMoveParticipants,
        breakoutRemoveAskForHelp,
        breakoutRequestGuestToken,
        closeBreakoutRoom,
        closeBreakouts,
        emptyBreakouts,
        guestLeaveBreakoutRoom,
    };
};
